import * as React from "react";
import "./TableForm.css";
import {
  GridRowsProp,
  GridRowModesModel,
  GridColDef,
  GridCellModesModel,
  GridCellModes,
  GridCellParams,
  DataGrid,
} from "@mui/x-data-grid";
import { isEmpty } from "../../../utils/object.utils";
import { FontFamily } from "../../../Constants/Enums/FontFamily.enums";
import HoverAwayListener from "../../HoverListener/HoverAwayListner";

declare module "@mui/x-data-grid" {
  interface ToolbarPropsOverrides {
    setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
    setRowModesModel: (
      newModel: (oldModel: GridRowModesModel) => GridRowModesModel,
    ) => void;
  }
}

interface ValidationProps {
  [key: string]: {
    validationFn: (updatingData?: any) => {
      isValid: boolean;
      errorMessage?: string;
    };
  };
}

interface Props {
  columns: GridColDef[];
  initialRow: GridRowsProp;
  onCellChange: (updateCell: any) => unknown;
  validations?: ValidationProps[] | null;
  isCellEditable?: (params: GridCellParams) => boolean;
}

export const TableFormV1 = ({
  columns,
  initialRow,
  onCellChange,
  validations = null,
  isCellEditable,
}: Props) => {
  const [rows, setRows] = React.useState<GridRowsProp>(initialRow);

  columns = columns.map((column) => {
    return {
      ...column,
      cellClassName: !column?.editable ? "readonly-cell" : "editable-cell ",
    };
  });

  React.useEffect(() => {
    if (!isEmpty(initialRow)) {
      setRows(initialRow);
    }
  }, [initialRow]);

  const [cellModesModel, setCellModesModel] =
    React.useState<GridCellModesModel>({});

  const handleCellClick = React.useCallback(
    (params: GridCellParams, event: React.MouseEvent) => {
      if (!params.isEditable) {
        return;
      }

      // Ignore portal
      if (
        (event.target as any).nodeType === 1 &&
        !event.currentTarget.contains(event.target as Element)
      ) {
        return;
      }

      setCellModesModel((prevModel) => {
        const prevValueToViewMode = Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {},
            ),
          }),
          {},
        );

        const currentValueToEditMode = {
          [params.id]: {
            // Revert the mode of other cells in the same row
            ...Object.keys(prevModel[params.id] || {}).reduce(
              (acc, field) => ({
                ...acc,
                [field]: { mode: GridCellModes.View },
              }),
              {},
            ),
            [params.field]: { mode: GridCellModes.Edit },
          },
        };

        return {
          ...prevValueToViewMode,
          ...currentValueToEditMode,
        };
      });
    },
    [],
  );

  const handleCellModesModelChange = React.useCallback(
    (newModel: GridCellModesModel) => {
      setCellModesModel(newModel);
    },
    [],
  );

  const handleRowUpdate = (newRow: any, oldRow: any) => {
    const updatedRow = { ...newRow, id: oldRow.id };

    const changedField = Object.keys(newRow).find(
      (key) => newRow[key] !== oldRow[key],
    ); // If required to change individual cell value

    if (validations && validations.length > 0) {
      for (let i = 0; i < validations.length; i++) {
        const validationObj = validations[i][changedField as any];

        if (validationObj) {
          const { isValid, errorMessage } = validationObj.validationFn(newRow);
          if (!isValid) {
            if (errorMessage) {
              alert(errorMessage);
            }
            return oldRow;
          }
        }
      }
    }

    setRows(rows.map((row) => (row.id === oldRow.id ? updatedRow : row)));

    onCellChange({ ...(updatedRow || {}), changedField });

    return updatedRow;
  };

  const onProcessRowUpdateError = (error: any) => {
    console.log("error", error);
  };

  const onHandleAway = (event: MouseEvent) => {
    const tableElement = document.querySelector(".MuiDataGrid-root");
    const dropdownElement = document.querySelector(".MuiMenu-list");

    // Check if the mouse is moving outside the table and not over the dropdown
    if (
      tableElement &&
      !tableElement.contains(event.relatedTarget as Node) && // Mouse is outside the table
      !dropdownElement // No dropdown is open
    ) {
      setCellModesModel((prevModel) => {
        const prevValueToViewMode = Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {},
            ),
          }),
          {},
        );
        return prevValueToViewMode;
      });
    }
  };
  return (
    <HoverAwayListener onHoverAway={onHandleAway}>
      <div style={{ height: "100%", width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          cellModesModel={cellModesModel}
          onCellModesModelChange={handleCellModesModelChange}
          onCellClick={handleCellClick}
          processRowUpdate={handleRowUpdate}
          hideFooterPagination
          hideFooterSelectedRowCount
          onProcessRowUpdateError={onProcessRowUpdateError}
          editMode="cell"
          style={{ fontSize: "14px", fontFamily: FontFamily.ROBOTO_REGULAR }}
          sx={SX_STYLE}
          isCellEditable={isCellEditable}
        />
      </div>
    </HoverAwayListener>
  );
};

const SX_STYLE = {
  "& .MuiDataGrid-cell": {
    fontSize: "14px", // Apply font size for all cells
    padding: "8px", // Apply padding for all cells
    border: "1px solid #ddd", // Add border to all cells
    textAlign: "center", // Center align text in all cells,
    "&:not(.MuiDataGrid-cell--editable)": {
      // pointerEvents: "none", // Disable pointer events
      cursor: "not-allowed",
      opacity: 0.6, // Make it look disabled
      backgroundColor: "#f0f0f0", // Light gray background
    },
  },
};
